import { useState } from 'react';
import { CheckCheck, ChevronDown, Minus, Plus, RotateCcw, SlidersHorizontal } from 'lucide-react';
import { READER_LIMITS, useReaderSettings } from '@/stores/reader.store';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

/**
 * 阅读器工具栏：排版设置 + 「标记读完」。
 *
 * 为什么排版设置放在工具栏而不是"设置页"：
 * 字号/行距是**读着读着才想调**的东西，调到满意往往要试三四次。放进设置页
 * 意味着"离开文章→调→回来"，上下文全丢了。这里的值只写本地（见 `reader.store`），
 * 改一次不发请求，所以可以放心给 ± 按钮。
 *
 * 为什么默认收起细节：窄屏上把 5 组控件全铺开会占掉大半屏，
 * 阅读页的主角必须是正文。摘要行（字号/行距/行宽）常显，需要调时再展开。
 */
export interface ReaderToolbarProps {
  /** 已标记读完（按钮变成"已读完"状态） */
  finished: boolean;
  /** 点「标记读完」：由页面弹出 1–5 自评的小表单 */
  onFinish: () => void;
  className?: string;
}

export function ReaderToolbar({ finished, onFinish, className }: ReaderToolbarProps) {
  const fontSize = useReaderSettings((s) => s.fontSize);
  const lineHeight = useReaderSettings((s) => s.lineHeight);
  const maxWidth = useReaderSettings((s) => s.maxWidth);
  const highlightLong = useReaderSettings((s) => s.highlightLong);
  const autoCollect = useReaderSettings((s) => s.autoCollect);
  const update = useReaderSettings((s) => s.update);
  const reset = useReaderSettings((s) => s.reset);

  const [expanded, setExpanded] = useState(false);

  return (
    <div className={cn('rounded-lg border bg-card/80 px-3 py-2 text-card-foreground backdrop-blur', className)}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
        >
          <SlidersHorizontal aria-hidden className="size-3.5" />
          排版
          <ChevronDown aria-hidden className={cn('size-3.5 transition-transform', expanded && 'rotate-180')} />
        </button>

        {/* 摘要行：收起时也让用户看得见当前值，否则"我调过什么"无从确认 */}
        <p className="text-[11px] text-muted-foreground">
          字号 {fontSize} · 行距 {lineHeight.toFixed(1)} · 行宽 {maxWidth}
        </p>

        <div className="ml-auto flex items-center gap-2">
          <Button
            type="button"
            variant={finished ? 'secondary' : 'outline'}
            size="sm"
            onClick={onFinish}
            disabled={finished}
          >
            <CheckCheck aria-hidden className="size-3.5" />
            {finished ? '已读完' : '标记读完'}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="mt-2 grid gap-2 border-t pt-2 sm:grid-cols-2 lg:grid-cols-3">
          <Stepper
            label="字号"
            value={`${fontSize}`}
            onDecrease={() => update({ fontSize: fontSize - READER_LIMITS.fontSize.step })}
            onIncrease={() => update({ fontSize: fontSize + READER_LIMITS.fontSize.step })}
            decreaseDisabled={fontSize <= READER_LIMITS.fontSize.min}
            increaseDisabled={fontSize >= READER_LIMITS.fontSize.max}
          />
          <Stepper
            label="行距"
            value={lineHeight.toFixed(1)}
            onDecrease={() => update({ lineHeight: stepLineHeight(lineHeight, -READER_LIMITS.lineHeight.step) })}
            onIncrease={() => update({ lineHeight: stepLineHeight(lineHeight, READER_LIMITS.lineHeight.step) })}
            decreaseDisabled={lineHeight <= READER_LIMITS.lineHeight.min}
            increaseDisabled={lineHeight >= READER_LIMITS.lineHeight.max}
          />
          <Stepper
            label="行宽"
            value={`${maxWidth}`}
            onDecrease={() => update({ maxWidth: maxWidth - READER_LIMITS.maxWidth.step })}
            onIncrease={() => update({ maxWidth: maxWidth + READER_LIMITS.maxWidth.step })}
            decreaseDisabled={maxWidth <= READER_LIMITS.maxWidth.min}
            increaseDisabled={maxWidth >= READER_LIMITS.maxWidth.max}
          />

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:col-span-2 lg:col-span-2">
            <Toggle
              label="高亮长难句"
              checked={highlightLong}
              onChange={(next) => update({ highlightLong: next })}
            />
            <Toggle
              label="点词自动加入生词本"
              checked={autoCollect}
              onChange={(next) => update({ autoCollect: next })}
            />
            <Button type="button" variant="ghost" size="sm" onClick={reset}>
              <RotateCcw aria-hidden className="size-3.5" />
              重置
            </Button>
          </div>

          <p className="text-[11px] text-muted-foreground sm:col-span-2 lg:col-span-3">
            {autoCollect
              ? '已开启自动收录：每次点词都会按「模糊」记进生词本，复习队列会长得比较快。'
              : '未开启自动收录：只有你主动点「不认识 / 模糊 / 认识」才会进生词本。'}
            排版设置只保存在这台设备上，改完立刻生效。
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * 行距步进时先抹掉浮点误差。
 *
 * `1.9 + 0.1` 在 IEEE754 下是 `2.0000000000000004`，直接塞进 CSS 变量虽然"能用"，
 * 但会让"是否已到上限"这类比较慢慢失准（点几下就少一格）。store 只负责夹范围，
 * 取整这一步放在这里更合适。
 */
function stepLineHeight(current: number, delta: number): number {
  return Math.round((current + delta) * 10) / 10;
}

/** 数值步进器。刻意不用 range：手机上拖动滑杆容易误触，± 一次一格更好控。 */
function Stepper({
  label,
  value,
  onDecrease,
  onIncrease,
  decreaseDisabled,
  increaseDisabled,
}: {
  label: string;
  value: string;
  onDecrease: () => void;
  onIncrease: () => void;
  decreaseDisabled: boolean;
  increaseDisabled: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-8 shrink-0 text-xs text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1">
        <IconButton label={`减小${label}`} onClick={onDecrease} disabled={decreaseDisabled}>
          <Minus aria-hidden className="size-3.5" />
        </IconButton>
        <span className="w-9 text-center text-xs tabular-nums">{value}</span>
        <IconButton label={`增大${label}`} onClick={onIncrease} disabled={increaseDisabled}>
          <Plus aria-hidden className="size-3.5" />
        </IconButton>
      </div>
    </div>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="grid size-7 place-items-center rounded-md border hover:bg-accent hover:text-accent-foreground disabled:opacity-40 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}

/** 开关。用 `role="switch"` + `aria-checked` 而不是 checkbox：语义更贴合"立即生效的设置"。 */
function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-2 text-xs hover:text-foreground"
    >
      <span
        aria-hidden
        className={cn(
          'relative h-4 w-7 shrink-0 rounded-full transition-colors',
          checked ? 'bg-primary' : 'bg-muted',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 size-3 rounded-full transition-all',
            checked ? 'left-3.5 bg-primary-foreground' : 'left-0.5 bg-card',
          )}
        />
      </span>
      {label}
    </button>
  );
}
